import { parseCalendarDate, parseInstant, parseTimeZone, instantFromEpochMilliseconds } from '@liquid-spirit/date-time/validation';
import { calendarDateAtInstant } from '@liquid-spirit/date-time/display';

/** The client resolves device/fixed mode; the server never guesses a device zone. */
export function homeCalendarQueryString(query = {}) {
  if (!query || typeof query !== 'object' || Array.isArray(query)
    || Object.keys(query).some(key => key !== 'displayTimeZone')) {
    throw new TypeError('Home calendar query accepts only displayTimeZone.');
  }
  if (query.displayTimeZone === undefined) return '';
  const zone = parseTimeZone(query.displayTimeZone);
  return `?displayTimeZone=${encodeURIComponent(zone)}`;
}

/** A client invalidation key, never a substitute for the server's current instant. */
export function homeCalendarRequestScope({ displayTimeZone, now = Date.now() } = {}) {
  const query = displayTimeZone === undefined ? {} : { displayTimeZone: parseTimeZone(displayTimeZone) };
  const zone = query.displayTimeZone || 'Australia/Melbourne';
  const today = calendarDateAtInstant(new Date(now).toISOString(), zone);
  return { query, today, key: JSON.stringify([query.displayTimeZone || 'legacyDefault', today]) };
}

/** Old servers may omit context; a new server must honour the explicit viewer query. */
export function assertHomeCalendarResponseScope(payload, scope) {
  const context = payload?.calendarContext;
  if (context && (!isHomeCalendarContext(context)
    || (scope.query.displayTimeZone !== undefined
      && (context.displayTimeZone !== scope.query.displayTimeZone || context.displayTimeZoneSource !== 'request')))) {
    throw Object.assign(new Error('Home response belongs to another calendar context.'), { code: 'INVALID_API_RESPONSE' });
  }
  return payload;
}

/** Expire cached projections at either viewer-day or community-reporting-month rollover. */
export function isHomeCalendarResponseCurrent(payload, scope, now = Date.now()) {
  try {
    assertHomeCalendarResponseScope(payload, scope);
    const context = payload?.calendarContext;
    return !context || (context.today === scope.today
      && context.reportingMonth === calendarDateAtInstant(new Date(now).toISOString(), context.reportingTimeZone).slice(0, 7));
  } catch { return false; }
}

/** Optional on old responses, complete and checked when returned by a new server. */
export const isHomeCalendarContext = value => {
  try {
    if (!value || typeof value !== 'object' || Array.isArray(value) || value.version !== 1
      || !['request', 'legacyDefault'].includes(value.displayTimeZoneSource)
      || !['community', 'legacyDefault'].includes(value.reportingTimeZoneSource)) return false;
    const displayZone = parseTimeZone(value.displayTimeZone);
    const reportingZone = parseTimeZone(value.reportingTimeZone);
    parseCalendarDate(value.today);
    const first = parseCalendarDate(`${value.reportingMonth}-01`);
    const start = parseInstant(value.reportingStartsAt), end = parseInstant(value.reportingEndsAt);
    const next = Number(first.slice(0, 4)) * 12 + Number(first.slice(5, 7));
    const nextDate = parseCalendarDate(`${String(Math.floor(next / 12)).padStart(4, '0')}-${String(next % 12 + 1).padStart(2, '0')}-01`);
    const before = instant => instantFromEpochMilliseconds(Date.parse(instant) - 1);
    // Verify the actual local month transitions without loading a scheduling
    // engine in app read bundles, including a skipped midnight/calendar day.
    return start < end && calendarDateAtInstant(start, reportingZone) >= first
      && calendarDateAtInstant(start, reportingZone) < nextDate
      && calendarDateAtInstant(before(start), reportingZone) < first
      && calendarDateAtInstant(end, reportingZone) >= nextDate
      && calendarDateAtInstant(before(end), reportingZone) < nextDate
      && (value.displayTimeZoneSource !== 'legacyDefault' || displayZone === 'Australia/Melbourne')
      && (value.reportingTimeZoneSource !== 'legacyDefault' || reportingZone === 'Australia/Melbourne');
  } catch { return false; }
};
